import {
  getQuestion,
  getRecordToken,
  sendAnswer,
  sendCertificateMessage,
  sendMessage,
  setIsRecording,
  setMessages,
  setStartTime,
  setSttErrorKey,
  setUserMessage,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useRef, useState} from "react";

import {useReportOptions} from "@hooks/Report";
import {setupSttInterceptors, sttApi} from "@http/stt";
import recImage from "@images/chat/rec-message.svg";
import sendMessageImage from "@images/chat/sending-message.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {ReportPopup} from "@popups/requestPopups/ReportPopup";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {FormError} from "@ui/forms/FormError";
import {BorderArticle} from "@ui/general/BorderArticle";
import {RootIcon} from "@ui/icons/RootIcon";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {Scrollbar} from "react-typescript-scrollbar";
import {v4 as uuidv4} from "uuid";

import styles from "./styles.module.scss";

export const Chat = ({
  hasAvatar = false,
  isAvatarTalking = false,
  avatarSessionState = "",
  AvatarSessionState = {},
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {t} = useTranslation();
  const textareaRef = useRef(null);
  const pendingTranscriptionRef = useRef(null);

  const {
    questions,
    scenario_session_id,
    product_session_id,
    certificate_session_id,
    answer_id,
    messages,
    userMessage,
    serverStatus,
    isRecording,
    recordToken,
    recordHost,
    sttErrorKey,
    sessionState,
  } = useSelector((state) => state.ml.chat);
  const {language} = useSelector((state) => state.common.settings);
  const {openPopup} = usePopup();
  const {SIMULATIONS_REPORT_OPTIONS} = useReportOptions();

  const [currentMessages, setCurrentMessages] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [, setAudioChunks] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState("24rem");
  const messagesCount = Array.isArray(messages) ? messages.length : 0;
  const isProductSessionEnded = product_session_id && sessionState === "ended";

  const isChatControlsDisabled =
    ((scenario_session_id || product_session_id || certificate_session_id) &&
      serverStatus === "loading") ||
    (product_session_id && messagesCount < 2) ||
    isProductSessionEnded;

  const {isMayoly, isJnj} = useSelector((state) => state.common.settings);
  const showProductConclusion = isMayoly || isJnj;

  useEffect(() => {
    setupSttInterceptors({
      onTokenRefreshed: () => {
        dispatch(getRecordToken());
      },
    });
  }, [dispatch]);

  const handleOpenPopup = () => {
    openPopup(<ReportPopup options={SIMULATIONS_REPORT_OPTIONS} />);
  };

  const isTest = window.location.host.includes("ava-test");

  // CONVERT AUDIO FILE
  const convertToWav = async (audioBlob) => {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const wavData = audioBufferToWav(audioBuffer);
    const wavBlob = new Blob([new Uint8Array(wavData)], {type: "audio/wav"});

    return new File([wavBlob], "audio_file_ava.wav", {type: "audio/wav"});
  };
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const {sampleRate} = buffer;
    const format = numChannels === 1 ? 1 : 2; // 1 for PCM, 2 for PCM stereo
    const byteRate = sampleRate * numChannels * 2; // 16-bit
    const dataLength = buffer.length * numChannels * 2; // 16-bit
    const wavBuffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(wavBuffer);

    // RIFF identifier
    writeString(view, 0, "RIFF");
    // file length minus RIFF identifier length and file description length
    view.setUint32(4, 36 + dataLength, true);
    // RIFF type
    writeString(view, 8, "WAVE");
    // format chunk identifier
    writeString(view, 12, "fmt ");
    // format chunk length
    view.setUint32(16, 16, true);
    // sample format (raw)
    view.setUint16(20, format, true);
    // channel count
    view.setUint16(22, numChannels, true);
    // sample rate
    view.setUint32(24, sampleRate, true);
    // byte rate (sample rate * block align)
    view.setUint32(28, byteRate, true);
    // block align (channel count * bytes per sample)
    view.setUint16(32, numChannels * 2, true);
    // bits per sample
    view.setUint16(34, 16, true);
    // data chunk identifier
    writeString(view, 36, "data");
    // data chunk length
    view.setUint32(40, dataLength, true);

    // Write the PCM samples
    floatTo16BitPCM(view, 44, buffer.getChannelData(0));

    return wavBuffer;
  };
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  };
  // CONVERT AUDIO FILE

  const handleChangeMessage = (e) => {
    setTextareaHeight("auto");
    setTextareaHeight(`${textareaRef.current.clientHeight}rem`);
    dispatch(setUserMessage(e.target.value));
    setTranscript(e.target.value);
    if (sttErrorKey) dispatch(setSttErrorKey(null));
  };

  const submitMessage = useCallback(
    async (messageText) => {
      const normalizedMessage = String(messageText || "").trim();
      if (!normalizedMessage) return;

      const user = {
        messageId: uuidv4(),
        sender: "user",
        text: normalizedMessage,
      };

      dispatch(setMessages(user));

      let resultAction;

      if (product_session_id) {
        resultAction = await dispatch(
          sendAnswer({
            id: product_session_id,
            answer_id,
            answer: user.text,
            showProductConclusion,
          }),
        );

        if (sendAnswer.fulfilled.match(resultAction)) {
          if (resultAction.payload?.state !== "ended" && questions > 0) {
            dispatch(getQuestion({id: product_session_id}));
          }

          dispatch(setUserMessage(""));
          stopRecording();

          if (transcript !== "") setTranscript("");
        } else console.log("error: send answer");
      } else if (certificate_session_id) {
        resultAction = await dispatch(
          sendCertificateMessage({
            id: certificate_session_id,
            msg: user.text,
          }),
        );

        if (sendCertificateMessage.fulfilled.match(resultAction)) {
          dispatch(setUserMessage(""));
          stopRecording();

          if (transcript !== "") setTranscript("");
        } else console.log("error: send message");
      } else if (scenario_session_id) {
        resultAction = await dispatch(
          sendMessage({id: scenario_session_id, msg: user.text}),
        );

        if (sendMessage.fulfilled.match(resultAction)) {
          dispatch(setUserMessage(""));
          stopRecording();

          if (transcript !== "") setTranscript("");
        } else console.log("error: send message");
      } else {
        console.log("error: send message");
      }
    },
    [
      answer_id,
      certificate_session_id,
      dispatch,
      showProductConclusion,
      product_session_id,
      questions,
      scenario_session_id,
      transcript,
    ],
  );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    await submitMessage(userMessage);
  };

  const startRecording = async () => {
    if (serverStatus === "loading" || loadingTranscript) return;

    try {
      setAudioFile(null);
      dispatch(setSttErrorKey(null));
      dispatch(getRecordToken());
      dispatch(setIsRecording(true));

      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      const recorder = new MediaRecorder(stream);

      setMediaRecorder(recorder);

      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, {type: "audio/webm"});
        console.log("[STT][Audio] recording stopped, blob ready", {
          type: audioBlob.type,
          sizeBytes: audioBlob.size,
          chunksCount: chunks.length,
        });
        const wavFile = await convertToWav(audioBlob);
        console.log("[STT][Audio] conversion completed", {
          type: wavFile.type,
          sizeBytes: wavFile.size,
          fileName: wavFile.name,
        });

        setAudioFile(wavFile);
        setAudioChunks([]);
      };

      recorder.start();
    } catch (err) {
      dispatch(setIsRecording(false));

      console.error("Error accessing microphone:", err);
    }
  };

  const stopRecording = () => {
    if (serverStatus === "loading" || loadingTranscript) return;

    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());

      dispatch(setIsRecording(false));
    }
  };

  useEffect(() => {
    if (!sttErrorKey) return;

    // STT токен/сервис недоступен — запись не имеет смысла, закрываем её.
    try {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }
    } catch (e) {
      console.warn("[STT][Audio] failed to stop recorder on STT error", e);
    } finally {
      dispatch(setIsRecording(false));
    }
  }, [dispatch, mediaRecorder, sttErrorKey]);

  const handleDownloadAudio = () => {
    if (!audioFile) return;

    let objectUrl = null;
    try {
      objectUrl = URL.createObjectURL(audioFile);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = audioFile.name || "audio_file_ava.wav";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("[STT][Audio] download triggered", {
        fileName: audioFile.name,
        fileType: audioFile.type,
        fileSizeBytes: audioFile.size,
      });
    } catch (error) {
      console.error("[STT][Audio] download failed", error);
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setAudioFile(null);
    }
  };

  const fetchAudio = async (host, file, token) => {
    if (file.size >= 24 * 1024 * 1024)
      return console.log("Размер файла превышает 24 МБ!");

    // https://speech-to-text.ml.vivanti.eu/transcribe
    // "en", "ru", "de", "es", "fr" - поддерживаемые языки

    const supportedLanguages = ["en", "ru", "de", "es", "fr"];
    let languageCode = language;

    if (supportedLanguages.includes(language)) languageCode = language;

    setLoadingTranscript(true);

    const formData = new FormData();

    formData.append("language", languageCode);
    formData.append("audiofile", file);
    console.log("[STT][Audio] sending to transcribe", {
      host,
      language: languageCode,
      fileName: file.name,
      fileType: file.type,
      fileSizeBytes: file.size,
    });

    try {
      const response = await sttApi.post("/transcribe", formData, {
        baseURL: host,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const transcribedText = String(response?.data?.text || "").trim();
      if (!transcribedText) return;

      setTranscript(transcribedText);
      dispatch(setUserMessage(transcribedText));
      await submitMessage(transcribedText);

      // Логика скачивания файла для тестов
      // const audioBlob = new Blob([file], { type: 'audio/wav' })
      // const url = URL.createObjectURL(audioBlob)
      // const a = document.createElement('a')

      // a.href = url
      // a.download = 'transcribed_audio.wav'

      // document.body.appendChild(a)
      // a.click()
      // document.body.removeChild(a)

      // URL.revokeObjectURL(url)
    } catch (err) {
      console.log("Ошибка транскрипции", err);
    } finally {
      setLoadingTranscript(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  useEffect(() => {
    if (!audioFile) return;

    const requestKey = `${audioFile.name}:${audioFile.size}:${audioFile.lastModified || 0}`;
    if (pendingTranscriptionRef.current === requestKey) return;

    if (!recordHost || !recordToken) {
      console.log("[STT][Audio] waiting transcribe prerequisites", {
        hasAudioFile: Boolean(audioFile),
        hasRecordHost: Boolean(recordHost),
        hasRecordToken: Boolean(recordToken),
      });
      return;
    }

    pendingTranscriptionRef.current = requestKey;
    void fetchAudio(recordHost, audioFile, recordToken);
  }, [audioFile, recordHost, recordToken]);

  // schedule message in scenario simulation
  // useEffect(() => {
  // 	if (
  // 		scenario_session_id &&
  // 		sessionState === 'ended' &&
  // 		(score > 0 || chatTime > 0)
  // 	) {
  // 		const text = t('page.chat.schedule_bot_message')

  // 		const scheduleMessage = {
  // 			messageId: uuidv4(),
  // 			text,
  // 			sender: 'bot',
  // 		}

  // 		dispatch(setBotMessage(text))
  // 		dispatch(setMessages(scheduleMessage))
  // 	}
  // }, [sessionState, scenario_session_id, score, chatTime])

  // last message in scenario simulation
  // useEffect(() => {
  // 	if (scenario_session_id && messages.length > 0 && time === 10000) {
  // 		const text = t('page.chat.final_bot_message')

  // 		const finalMessage = {
  // 			messageId: uuidv4(),
  // 			text,
  // 			sender: 'bot',
  // 		}

  // 		dispatch(setBotMessage(text))
  // 		dispatch(setMessages(finalMessage))
  // 	}
  // }, [time, scenario_session_id])

  useEffect(() => {
    setTextareaHeight(
      userMessage ? `${textareaRef.current.scrollHeight}rem` : "24rem",
    );
  }, [userMessage]);

  useEffect(() => {
    if (!product_session_id && !scenario_session_id && !certificate_session_id)
      stopRecording();
  }, [product_session_id, scenario_session_id, certificate_session_id]);

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [location]);

  useEffect(() => {
    const chat = document.querySelector(".rcs-inner-container");
    if (!chat) return;

    chat.scrollTop = chat.scrollHeight;

    if (textareaRef.current) {
      if (scenario_session_id || certificate_session_id) {
        textareaRef.current.focus();
      } else if (product_session_id && messages.length > 2) {
        textareaRef.current.focus();
      }
    }

    if (messages.length === 1) {
      const millisecondTimestamp = moment().valueOf();

      dispatch(setStartTime(millisecondTimestamp));
    }
  }, [
    messages,
    scenario_session_id,
    product_session_id,
    certificate_session_id,
  ]);

  useEffect(() => {
    const isAvatarConnected =
      hasAvatar && avatarSessionState === AvatarSessionState.CONNECTED;

    if (isAvatarConnected && isAvatarTalking) {
      return;
    }

    const visibleMessages =
      product_session_id || showProductConclusion
        ? messages
        : messages.filter((m) => m.kind !== "conclusion");

    setCurrentMessages(visibleMessages);
  }, [
    messages,
    product_session_id,
    showProductConclusion,
    isAvatarTalking,
    hasAvatar,
    avatarSessionState,
  ]);

  return (
    <BorderArticle>
      <div className={styles.gameChat}>
        <div className={styles.gameChatWrapper}>
          <Scrollbar
            units={"rem"}
            contentHeight={"auto"}
            contentPadding={10}
            barColor={"var(--greyLight)"}
            barBorderColor={
              "linear-gradient(180deg, var(--border) 65%, transparent)"
            }
            barWidth={6}
            barRadius={8}
            thumbColor={
              "linear-gradient(0deg, rgb(209, 139, 203) 0%, rgb(209, 186, 222) 50%, rgb(209, 139, 203) 100%)"
            }
            thumbWidth={6}
            thumbRadius={8}
            keepItBottom
          >
            <div className={styles.chat}>
              {currentMessages &&
                currentMessages.length > 0 &&
                currentMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={
                      msg.kind === "conclusion"
                        ? styles.conclusionMessage
                        : msg.kind === "product_session_complete"
                          ? styles.botMessage
                          : msg.sender === "bot"
                            ? styles.botMessage
                            : styles.userMessage
                    }
                  >
                    <RootDesc>
                      {msg.kind === "conclusion" ? (
                        <b>{msg.text}</b>
                      ) : msg.kind === "product_session_complete" ? (
                        <b>{t("button.product_session_questions_finished")}</b>
                      ) : msg.sender === "bot" ? (
                        <b>{msg.text}</b>
                      ) : (
                        <span>{msg.text}</span>
                      )}
                    </RootDesc>

                    {msg.sender !== "bot" && msg.kind !== "conclusion" && (
                      <img
                        className={styles.userMessageIcon}
                        src={sendMessageImage}
                        alt={"sending"}
                      />
                    )}
                  </div>
                ))}
            </div>
          </Scrollbar>

          <div
            className={
              isChatControlsDisabled
                ? `${styles.chatControlsDisabled} ${styles.chatControls}`
                : styles.chatControls
            }
          >
            <div className={styles.textarea}>
              {sttErrorKey && (
                <FormError
                  error={t(sttErrorKey)}
                  bottom={-34}
                  center
                />
              )}
              <textarea
                disabled={
                  serverStatus === "loading" || sessionState === "ended"
                    ? true
                    : false
                }
                ref={textareaRef}
                type={"text"}
                value={userMessage}
                onChange={handleChangeMessage}
                onKeyDown={handleKeyDown}
                style={{
                  height: textareaHeight,
                  maxHeight: "140rem",
                  overflowY: "scroll",
                }}
              />
            </div>

            <div className={styles.chatControlsBtns}>
              {serverStatus === "loading" || loadingTranscript ? (
                <div
                  className={styles.spinner}
                  style={{pointerEvents: "none"}}
                >
                  <RootIcon
                    width={26}
                    height={26}
                    id={"spinner-upload"}
                  />
                </div>
              ) : (
                <div
                  onClick={handleSendMessage}
                  style={
                    isRecording || sessionState === "ended"
                      ? {
                          opacity: "0.5",
                          pointerEvents: "none",
                          animation: "none",
                        }
                      : {
                          opacity: "1",
                          pointerEvents: "all",
                          animation: "none",
                        }
                  }
                  className={styles.spinner}
                >
                  <RootIcon
                    width={26}
                    height={26}
                    id={"send-message"}
                  />
                </div>
              )}

              <div
                className={
                  !isRecording
                    ? styles.rec
                    : `${styles.rec} ${styles.recActive}`
                }
                onClick={
                  sessionState === "ended"
                    ? undefined
                    : !isRecording && !loadingTranscript
                      ? startRecording
                      : stopRecording
                }
              >
                <button
                  disabled={
                    serverStatus === "loading" ||
                    loadingTranscript ||
                    sessionState === "ended"
                      ? true
                      : false
                  }
                  type={"button"}
                >
                  <img
                    src={recImage}
                    alt={"rec"}
                  />
                </button>
              </div>

              {isTest && audioFile && !isRecording && (
                <div
                  className={`${styles.spinner} ${styles.downloadFile}`}
                  onClick={handleDownloadAudio}
                  style={
                    serverStatus === "loading" || loadingTranscript
                      ? {
                          opacity: "0.5",
                          pointerEvents: "none",
                          animation: "none",
                        }
                      : {
                          opacity: "1",
                          pointerEvents: "all",
                          animation: "none",
                        }
                  }
                >
                  <RootIcon
                    width={22}
                    height={22}
                    id={"download-file"}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.reportBtn}>
          <ControlButton
            color={"var(--black)"}
            txt={t("button.report")}
            iconId={"report"}
            onClick={handleOpenPopup}
          />
        </div>
      </div>
    </BorderArticle>
  );
};
